import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { updateTitle } from 'store/app';
import { assetDetails } from 'store/asset';
import { getImage } from 'hooks/get-image';
import { Icon } from '@iconify/react';
import AnimatedTabs from 'components/ui/AnimatedTabs';
import InfoCard from 'components/cards/InfoCard';
import Button from 'components/ui/Button';
import ReviewButton from 'components/common/ReviewButton';
import cn from 'classnames';
import CategoryBadge from 'components/common/CategoryBadge';
import { available } from 'config/asset';
import { createRequest } from 'store/request';
import { motion, AnimatePresence } from 'framer-motion';

const AssetDetails = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selected } = useSelector(state => state.asset);
  const { user } = useSelector(state => state.auth);

  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const items = [
    {
      title: t('asset.space.label'),
      text: t('asset.space.text'),
      info: selected?.additional?.volume,
      value: 'm²',
    },
    {
      title: t('asset.price.label'),
      text: t('asset.price.text'),
      info: selected?.additional?.price,
      value: '€',
    },
    {
      title: t('asset.weight.label'),
      text: t('asset.weight.text'),
      info: selected?.additional?.weight,
      value: 'kg',
    },
  ];

  const tabs = [
    {
      title: t('asset.description'),
      content:
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-5">
            {items.map((item, index) => (
              <InfoCard key={index} data={item} />
            ))}
          </div>
          <div dangerouslySetInnerHTML={{ __html: JSON.parse(selected?.description?.about || '""') }} />
        </div>
    },
    {
      title: t('asset.included'),
      content: <div dangerouslySetInnerHTML={{ __html: JSON.parse(selected?.description?.included || '""') }} />
    },
    {
      title: t('asset.know'),
      content: <div dangerouslySetInnerHTML={{ __html: JSON.parse(selected?.description?.know || '""') }} />
    },
  ];

  const fetchDetails = (loading) => {
    setLoading(loading);
    dispatch(assetDetails(id))
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNotification = () => {
    if (selected.requested) {
      navigate('/owned')
    } else {
      setIsSubmitting(true);
      dispatch(createRequest({ asset: selected.id }))
        .then((res) => {
          if (res.payload.Result) {
            fetchDetails(false);
          }
        })
        .finally(() => {
          // Set a slight delay before removing the submitting state to allow the animation to complete
          setTimeout(() => {
            setIsSubmitting(false);
          }, 300);
        });
    }
  }

  useEffect(() => {
    fetchDetails(true);
    dispatch(updateTitle(t('asset.details')));
  }, []);

  if (loading) {
    return (
      <div className="px-8 flex flex-col gap-8 h-full overflow-y-scroll pb-6 animate-pulse">
        <div className="h-44 bg-gray-300 rounded" />
        <div className="flex flex-col gap-2">
          <div className="h-5 bg-gray-300 w-1/3 rounded" />
          <div className="h-5 bg-gray-300 w-2/3 rounded" />
        </div>
        <div className="grid grid-cols-3 gap-5">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-gray-300 rounded" />
          ))}
        </div>
        <div className="h-64 bg-gray-300 rounded" />
        <div className="h-12 bg-gray-300 rounded mt-4" />
      </div>
    );
  }

  return (
    selected &&
    <div className="px-8 flex flex-col gap-8 h-full overflow-y-scroll pb-6">
      <div className="flex justify-center items-center">
        <img
          alt={selected.id}
          src={getImage(selected.files)}
          className={cn(
            'w-44',
            {
              'grayscale opacity-50': !available.includes(selected.state) || !selected.enabled
            }
          )}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <CategoryBadge category={selected.category} />
            {!available.includes(selected.state) &&
              <span className="text-xs text-yellow-500 text-truncate">
                {selected.expiresAt || '--'}
              </span>
            }
          </div>
          <div className="flex items-center gap-1 font-semibold">
            <Icon icon="ic:round-star-half" width="20" />
            <span>({selected.average ?? '--'})</span>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-semibold text-2xl">{selected?.name}</span>
          <ReviewButton text={t('review.label', { count: 2 })} onClick={() => navigate('/asset/comments/' + selected?.id)} />
        </div>
      </div>
      <div className="h-full">
        <AnimatedTabs tabs={tabs} />
      </div>
      <div>
        {(available.includes(selected.state) && selected.enabled) ? (
          <Button
            onClick={() => navigate('/asset/pickup/' + selected.id)}
            color="primary"
            size="lg"
            text={t('asset.pickup_asset')}
          />
        ) : (
          <AnimatePresence mode="wait">
            {isSubmitting ? (
              <motion.div
                key="submitting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full"
              >
                <Button
                  color="secondary"
                  size="lg"
                  className="w-full relative overflow-hidden"
                  isDisabled={true}
                >
                  <motion.div 
                    className="flex items-center justify-center w-full gap-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t('request.processing')}</span>
                  </motion.div>
                  <motion.div
                    className="absolute bottom-0 left-0 h-1 bg-white bg-opacity-30"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 1.5 }}
                  />
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="normal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full"
              >
                <Button
                  onClick={handleNotification}
                  color={selected.requested ? "dark" : "secondary"}
                  outlined={selected.requested}
                  size="lg"
                  text={selected.requested ? t('request.no_notify') : t('request.notify')}
                  isDisabled={selected.current && selected.current.id === user.id}
                  className="w-full"
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default AssetDetails;